import { useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidPhoneNumber } from 'libphonenumber-js/max';

import { CallPlanForm } from 'features/care-connect-settings/domain/models/call-plan-form';
import { useIsNumberUnique } from 'features/care-connect-settings/domain/queries/number-queries';

type ExternalNumberValidation = {
  getValidationRules: (
    index: number,
    initialNumber: string | undefined,
  ) => {
    required: string;
    validate: {
      length: (value: string) => string | true;
      format: (value: string) => string | true;
      isUnique: (value: string, formValues: CallPlanForm) => Promise<string | true>;
    };
  };
  handleDigitsOnlyChange: (value: string, onChange: (value: string) => void) => void;
  handleBeforeInput: (event: FormEvent<HTMLInputElement>) => void;
};

const useExternalNumberValidation = (): ExternalNumberValidation => {
  const { t } = useTranslation('careConnectSettings', { keyPrefix: 'callPlanExternalNumbers.validationRules' });
  const { mutateAsync: isNumberUnique } = useIsNumberUnique();
  const uniquenessCache = useRef<Map<string, string | true>>(new Map());

  const validateLength = (value: string): string | true => {
    if (value.length < 2 || value.length > 15) {
      return t('lengthBetweenTwoAndFifteen');
    }

    return true;
  };

  const validateFormat = (value: string): string | true => {
    if (!isValidPhoneNumber(`+${value}`)) {
      return t('invalidItuNumber');
    }

    return true;
  };

  const validateUnique = async (
    value: string,
    formValues: CallPlanForm,
    index: number,
    initialNumber: string | undefined,
  ): Promise<string | true> => {
    const matchesLocalNumber = formValues.externalNumbers.some(
      (externalNumber, externalNumberIndex) => externalNumberIndex !== index && externalNumber.number === value,
    );
    if (matchesLocalNumber) {
      return t('numberAlreadyInUse');
    }

    if (value === initialNumber) {
      return true;
    }

    const cachedResult = uniquenessCache.current.get(value);
    if (cachedResult !== undefined) {
      return cachedResult;
    }

    try {
      const validationResult = (await isNumberUnique(value)) || t('numberAlreadyInUse');
      uniquenessCache.current.set(value, validationResult);
      return validationResult;
    } catch {
      return true;
    }
  };

  const getValidationRules = (index: number, initialNumber: string | undefined) => ({
    required: t('fieldRequired'),
    validate: {
      length: validateLength,
      format: validateFormat,
      isUnique: (value: string, formValues: CallPlanForm) => validateUnique(value, formValues, index, initialNumber),
    },
  });

  const handleDigitsOnlyChange = (value: string, onChange: (value: string) => void): void => {
    onChange(value.replaceAll(/\D/g, ''));
  };

  const handleBeforeInput = (event: FormEvent<HTMLInputElement>): void => {
    const inputEvent = event.nativeEvent as InputEvent;
    if (inputEvent.data && !/^\d+$/.test(inputEvent.data)) {
      event.preventDefault();
    }
  };

  return {
    getValidationRules,
    handleDigitsOnlyChange,
    handleBeforeInput,
  };
};

export default useExternalNumberValidation;
